import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Spinner } from '@structural-climbing/ui'
import { useGymThemeContext } from '../../layouts/useGymTheme'
import { MemberIdentity } from '../members/MemberIdentity'
import { MemberProfileModal } from '../members/MemberProfileModal'
import { useTrainers } from './useTrainers'

/**
 * What a gym owner sees on the My Members page.
 *
 * Owners get every nav item, so they land here with no caseload of their own.
 * Showing them an empty list would imply they have trainees and happen to have
 * none; showing a load error, which is what happened before, is worse. Explain
 * whose page it is and show who currently has it.
 */
export function TrainersOverview() {
  const { t } = useTranslation()
  const { primaryColor } = useGymThemeContext()
  const { trainers, loading } = useTrainers()
  const [profileTarget, setProfileTarget] = useState<string | null>(null)

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size="sm" color="primary" />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-foreground/60">{t('myMembers.ownerExplainer')}</p>

      {trainers.length === 0 ? (
        <p className="text-sm text-foreground/40">{t('myMembers.ownerNoTrainers')}</p>
      ) : (
        <div>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-foreground/40">
            {t('myMembers.ownerTrainersTitle')}
          </p>
          <div className="flex flex-col divide-y divide-divider">
            {trainers.map((trainer) => (
              <div key={trainer.user_id} className="py-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                  <MemberIdentity
                    userId={trainer.user_id}
                    name={trainer.name}
                    nickname={trainer.nickname}
                    email={trainer.email}
                    avatarExt={trainer.avatar_ext}
                    avatarUpdatedAt={trainer.avatar_updated_at}
                    primaryColor={primaryColor}
                  />
                  <span className="shrink-0 text-sm text-foreground/50">
                    {t('myMembers.ownerAssignedCount', { count: trainer.assigned_count })}
                  </span>
                </div>

                {/* Who they actually coach. Same row treatment as the member
                    lists elsewhere, and the same profile modal on click, so an
                    owner is never looking at a name they cannot open. */}
                {trainer.members.length > 0 && (
                  <div className="mt-2 ml-4 flex flex-col gap-1.5">
                    {trainer.members.map((m) => (
                      <button
                        key={`${trainer.user_id}-${m.child_id ?? m.user_id}`}
                        type="button"
                        title={t('members.viewProfile')}
                        onClick={() => setProfileTarget(m.user_id)}
                        className="flex w-full min-w-0 cursor-pointer items-center gap-2 rounded-lg bg-content2/50 px-3 py-2 text-left"
                      >
                        <MemberIdentity
                          userId={m.user_id}
                          name={m.name}
                          nickname={m.nickname}
                          email={m.email}
                          avatarExt={m.avatar_ext}
                          avatarUpdatedAt={m.avatar_updated_at}
                          primaryColor={primaryColor}
                        />
                        {m.child_name && (
                          <span className="shrink-0 rounded-full bg-primary/10 px-1.5 py-0.5 text-xs text-primary">
                            {m.child_name}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {profileTarget && (
        <MemberProfileModal userId={profileTarget} onClose={() => setProfileTarget(null)} />
      )}
    </div>
  )
}
